import { z } from "zod";

export const roles = [
  "platform_admin",
  "provisioning_agent",
  "billing_agent",
  "support_agent",
  "read_only_auditor",
] as const;
export type Role = (typeof roles)[number];

export const permissions = [
  "account:read",
  "account:write",
  "location:write",
  "e911:validate",
  "catalog:read",
  "catalog:write",
  "provisioning:write",
  "billing:read",
  "billing:write",
  "device:diagnose",
  "routing:write",
  "audit:read",
] as const;
export type Permission = (typeof permissions)[number];

const grants: Record<Role, readonly Permission[]> = {
  platform_admin: permissions,
  provisioning_agent: [
    "account:read",
    "account:write",
    "location:write",
    "e911:validate",
    "catalog:read",
    "provisioning:write",
    "device:diagnose",
    "routing:write",
    "billing:read",
    "audit:read",
  ],
  billing_agent: [
    "account:read",
    "catalog:read",
    "billing:read",
    "billing:write",
    "audit:read",
  ],
  support_agent: [
    "account:read",
    "catalog:read",
    "billing:read",
    "device:diagnose",
    "routing:write",
    "audit:read",
  ],
  read_only_auditor: [
    "account:read",
    "catalog:read",
    "billing:read",
    "audit:read",
  ],
};

export function can(role: Role, permission: Permission): boolean {
  return grants[role].includes(permission);
}

export const accountKinds = [
  "residential",
  "commercial",
  "enterprise",
] as const;
export const accountStatuses = [
  "draft",
  "pending_activation",
  "active",
  "suspended",
  "pending_disconnect",
  "disconnected",
] as const;
export const orderStatuses = [
  "draft",
  "submitted",
  "running",
  "waiting_external",
  "action_required",
  "completed",
  "failed",
  "canceled",
  "compensating",
] as const;
export const stepStatuses = [
  "pending",
  "running",
  "waiting",
  "succeeded",
  "failed_retryable",
  "failed_terminal",
  "skipped",
  "compensated",
] as const;

export type AccountKind = (typeof accountKinds)[number];
export type AccountStatus = (typeof accountStatuses)[number];
export type OrderStatus = (typeof orderStatuses)[number];
export type StepStatus = (typeof stepStatuses)[number];

export const uuidSchema = z.string().uuid();
export const idempotencyKeySchema = z.string().trim().min(8).max(160);
export const e164Schema = z
  .string()
  .regex(/^\+[1-9]\d{7,14}$/, "Use E.164 format, for example +12705550148");
export const macAddressSchema = z
  .string()
  .transform((value) => value.replace(/[^a-fA-F0-9]/g, "").toUpperCase())
  .pipe(z.string().regex(/^[A-F0-9]{12}$/, "Enter a 12-digit MAC address"));

export const postalAddressSchema = z.object({
  line1: z.string().trim().min(3).max(120),
  line2: z.string().trim().max(120).optional(),
  city: z.string().trim().min(2).max(80),
  region: z
    .string()
    .trim()
    .length(2)
    .transform((value) => value.toUpperCase()),
  postalCode: z
    .string()
    .trim()
    .regex(/^\d{5}(?:-\d{4})?$/),
  country: z.literal("US").default("US"),
});

export const createAccountSchema = z.object({
  kind: z.enum(accountKinds),
  name: z.string().trim().min(2).max(160),
  contactName: z.string().trim().min(2).max(120),
  billingEmail: z.string().trim().email().max(254),
});

export const createLocationSchema = z.object({
  name: z.string().trim().min(2).max(100),
  address: postalAddressSchema,
});

export const createUserSchema = z.object({
  displayName: z.string().trim().min(2).max(120),
  extension: z
    .string()
    .trim()
    .regex(/^\d{2,8}$/),
  roleLabel: z.string().trim().min(2).max(80).default("User"),
});

export const createDeviceSchema = z.object({
  model: z.string().trim().min(2).max(100),
  macAddress: macAddressSchema,
  lineId: uuidSchema.optional(),
});

export const activationCommandSchema = z.object({
  accountId: uuidSchema,
  locationId: uuidSchema,
  planId: uuidSchema,
  quantity: z.number().int().min(1).max(500),
  numberRequest: z.discriminatedUnion("mode", [
    z.object({
      mode: z.literal("reserve"),
      areaCode: z.string().regex(/^\d{3}$/),
    }),
    z.object({
      mode: z.literal("port"),
      number: e164Schema,
      authorizedName: z.string().trim().min(2),
      serviceAddress: postalAddressSchema,
    }),
  ]),
  device: z
    .object({ model: z.string().min(2), macAddress: macAddressSchema })
    .optional(),
});
export type ActivationCommand = z.infer<typeof activationCommandSchema>;

export type ProblemDetails = {
  type: string;
  title: string;
  status: number;
  code: string;
  detail: string;
  correlationId: string;
  retryable: boolean;
  fieldErrors?: Record<string, string[]>;
};

export type OperatorIdentity = {
  id: string;
  providerId: string;
  displayName: string;
  email: string;
  role: Role;
};

export const activationSteps = [
  "validate_request",
  "validate_e911",
  "create_billing",
  "provision_number",
  "store_desired_voice",
  "provision_device",
  "reconcile_voice",
  "activate_service",
] as const;
export type ActivationStep = (typeof activationSteps)[number];

export type WorkflowFailure = {
  code: "PORT_CSR_MISMATCH" | "ADAPTER_RETRY_EXHAUSTED" | "VALIDATION_FAILED";
  message: string;
  retryable: boolean;
  correctionFields?: readonly string[];
};

export type ActivationWorkflowState = {
  orderId: string;
  status: OrderStatus;
  currentStep: ActivationStep | null;
  completedSteps: ActivationStep[];
  attempts: Partial<Record<ActivationStep, number>>;
  failure: WorkflowFailure | null;
  desiredConfigurationVersion: number;
  observedConfigurationVersion: number;
};

export function initialActivationState(
  orderId: string,
): ActivationWorkflowState {
  return {
    orderId,
    status: "submitted",
    currentStep: "validate_request",
    completedSteps: [],
    attempts: {},
    failure: null,
    desiredConfigurationVersion: 0,
    observedConfigurationVersion: 0,
  };
}

export type StepOutcome =
  | {
      kind: "succeeded";
      observedConfigurationVersion?: number;
      desiredConfigurationVersion?: number;
    }
  | {
      kind: "retryable_failure";
      code: "ADAPTER_RETRY_EXHAUSTED";
      message: string;
    }
  | {
      kind: "action_required";
      code: "PORT_CSR_MISMATCH" | "VALIDATION_FAILED";
      message: string;
      correctionFields: readonly string[];
    };

export function applyStepOutcome(
  state: ActivationWorkflowState,
  step: ActivationStep,
  outcome: StepOutcome,
): ActivationWorkflowState {
  if (state.currentStep !== step) {
    throw new Error(
      `Cannot apply ${step}; current step is ${state.currentStep ?? "none"}`,
    );
  }
  if (state.completedSteps.includes(step)) return state;

  const attempts = {
    ...state.attempts,
    [step]: (state.attempts[step] ?? 0) + 1,
  };
  if (outcome.kind === "action_required") {
    return {
      ...state,
      status: "action_required",
      attempts,
      failure: {
        code: outcome.code,
        message: outcome.message,
        retryable: true,
        correctionFields: outcome.correctionFields,
      },
    };
  }
  if (outcome.kind === "retryable_failure") {
    return {
      ...state,
      status: "failed",
      attempts,
      failure: {
        code: outcome.code,
        message: outcome.message,
        retryable: true,
      },
    };
  }

  const completedSteps = [...state.completedSteps, step];
  const nextStep = activationSteps[activationSteps.indexOf(step) + 1] ?? null;
  return {
    ...state,
    status: nextStep ? "running" : "completed",
    currentStep: nextStep,
    completedSteps,
    attempts,
    failure: null,
    desiredConfigurationVersion:
      outcome.desiredConfigurationVersion ?? state.desiredConfigurationVersion,
    observedConfigurationVersion:
      outcome.observedConfigurationVersion ??
      state.observedConfigurationVersion,
  };
}

export function resumeAfterCorrection(
  state: ActivationWorkflowState,
): ActivationWorkflowState {
  if (state.status !== "action_required" || !state.currentStep) {
    throw new Error("Only an action-required workflow can be resumed");
  }
  return { ...state, status: "running", failure: null };
}

export function deterministicResourceKey(input: {
  providerId: string;
  accountId: string;
  orderId: string;
  activity: string;
  resource: string;
}): string {
  return [
    input.providerId,
    input.accountId,
    input.orderId,
    input.activity,
    input.resource,
  ].join("/");
}

const redactedKeys =
  /authorization|password|secret|token|sip[_-]?credential|payment[_-]?data|signed[_-]?url/i;

export function redact<T>(value: T): T {
  if (Array.isArray(value)) return value.map((entry) => redact(entry)) as T;
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, entry]) => [
        key,
        redactedKeys.test(key) ? "[REDACTED]" : redact(entry),
      ]),
    ) as T;
  }
  return value;
}
export * from "./equipment-catalog";
export * from "./dialing";
